import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/tmp/77e67204/node_modules/gatsby-theme-carbon/src/templates/Default.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">General guidance </AnchorLink>
      <AnchorLink mdxType="AnchorLink">Format</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Form logic</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Validation & errors</AnchorLink>
    </AnchorLinks>
    <h2 {...{
      "id": "general-guidance"
    }}>{`General guidance`}</h2>
    <h3 {...{
      "id": "keep-it-short"
    }}>{`Keep it short`}</h3>
    <p><em parentName="p">{`Forms`}</em>{` are used for submitting data so be as concise as possible when designing. Think about each field and what value the data will provide. What do you gain by collecting this information?`}</p>
    <p><strong parentName="p">{`Begin by asking:`}</strong></p>
    <ul>
      <li parentName="ul">{`Is this a piece of information that is valuable to us?`}</li>
      <li parentName="ul">{`Is this a piece of information that is so valuable that it’s worth preventing the user from continuing if they choose not to provide it?`}</li>
    </ul>
    <h3 {...{
      "id": "labels"
    }}>{`Labels`}</h3>
    <p>{`Effective form labeling helps users understand what information to enter into a Text Input. Using a placeholder text as a label is often applied as a space-saving method. However, this is not recommended because it hides context and presents accessibility issues.`}</p>
    <p><strong parentName="p">{`Accessibility best practices for labels:`}</strong></p>
    <ul>
      <li parentName="ul">{`Labels must be visible when an input gets focus.`}</li>
      <li parentName="ul">{`Labels must be announced to the screen reader on focus.`}</li>
      <li parentName="ul">{`Ensure the helper text that appears under an input is read when an assistive technology user stops at an input using ARIA.`}</li>
      <li parentName="ul">{`Use sentence-style capitalization (only the first word in a phrase and any proper nouns capitalized).`}</li>
    </ul>
    <h3 {...{
      "id": "default-values"
    }}>{`Default values`}</h3>
    <p>{`Where possible, add programmatic assistance. Detect and pre-fill inputs to reduce errors and save time. When the software can’t determine the value that belongs in an input, use type-ahead to make suggestions. Use sentence-case for default values, detected values, and auto-completion text.`}</p>
    <h3 {...{
      "id": "help-text"
    }}>{`Help text`}</h3>
    <p>{`Help text is pertinent information that assists the user in completing a field. Help text is always available when the input is focused and appears underneath the label. Use sentence-style capitalization, and in most cases, write the text as full sentences with punctuation.`}</p>
    <h3 {...{
      "id": "placeholder-text"
    }}>{`Placeholder text`}</h3>
    <p>{`Placeholder text provides hints or examples of what to enter. Placeholder text disappears after the user begins entering data into the Input and should not contain crucial information. Use sentence-style capitalization, and in most cases, write the text as a direct statement without punctuation.`}</p>
    <h2 {...{
      "id": "format"
    }}>{`Format`}</h2>
    <p>{`All forms are comprised of 6 elements:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Labels:`}</strong>{` Inform users what the corresponding input fields mean.`}</li>
      <li parentName="ul"><strong parentName="li">{`Input fields:`}</strong>{` Enable users to provide information. Information can be entered through a variety of different input fields ranging from text fields, checkboxes, and many other types.`}</li>
      <li parentName="ul"><strong parentName="li">{`Help text:`}</strong>{` Provides assistance on how to fill out a field. Help text is optional.`}</li>
      <li parentName="ul"><strong parentName="li">{`Placeholder text:`}</strong>{` Hints at what goes into a field. Placeholder text is optional.`}</li>
      <li parentName="ul"><strong parentName="li">{`Actions:`}</strong>{` Allow users to submit a form.`}</li>
      <li parentName="ul"><strong parentName="li">{`Validation:`}</strong>{` Ensures the data submitted by the user conforms to acceptable parameters.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "85.32608695652173%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAARCAYAAADdRIy+AAAACXBIWXMAAAsSAAALEgHS3X78AAAC/klEQVQ4y51US0wTURR9LWCnBWK0IC5I/C0EETXGhQ2prU5jdWHqp8LGKjW6LwSb2iYIKUFoQyBiSDQiBBeF2gRFhRhTvgGlgB06MEzbqaEFwmcBK9PleGdCCWRKF97k5LyZ9+6Z+8598xDaDpZld5jDwsICCofDKBaLoenpaTQ2NiYeHR0Vj4+Pi9bW1lAoFEIMw/A5Cf7v4D6wsbGxI8R9WBB2u53nuro6aXV19ZFoNJoJVWRChVKoTjY0NIQPDw/fHhwcPL07L6kYF01NTQnOcjgc+YuLiwdBUL60tITRNI37/X4vVDZAkuQAVOYBIY7LtrcsFgg2Njby3NzcLKqtrU3nfARB2erqagaInZiZmTH4fL7Subk5JQhcBmgAx1J6WF9fv+cZGnNgfX1d5vF4DkMz7kGFT6Cy8/F4PDuxJhKJpN5yS0sLcrlc/NjpdIqgUgyGmNfrfbC1tfVieXm5AWABaACZ3DpgoaBGo+FZq9UihUKB9Ho9kkgkO/PgnQ62b4bkZ4DHgAucv/sKcqFSqfY9KgRBFECjrqysrBTDOTwDKITqRdzc5uamMEGtVvOM43haSUmJTKlUYuXl5dj2ubs4MTFhh4Ntg/E18K0I/L0E3c/n5imKEgq2trby3NbWJgXv8sDT3K6urtxfk5NpQZrWkoGAd5Yg+qj5+e8RhvkBJ+BnMBh8yOWAsDhlY5JF95e+U739/eeev+3I2v0+RvhESRMqKip4tlgsmNFoPGo2mw/9YRi+i2GSvEVOTUVnJ30h2v/bH6VIb5QKTMWogJEXpQLCCt1ud4IzAFngC0ZTlBSMT+/t6DzZ295R5nr96s6H9s/3e/pCZZ3uecO7buosl/O+hxLt+6eAf9Kampq8kZGRbD9BZIOg+K5ejzc4nF9vXFd9LNC99Jg+sT05lew39Igt5ZOessk91Ol0Sd/jarWkqLAo5+ZVhVxheCNHBlZ+vPJvTrE1LuXmCyxxYZLVauXZZrOhqqoqBLcLfy+aTKaUV1leJbvn+R9GSKyS1nr+pgAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "effective form design",
            "title": "effective form design",
            "src": "/static/0fe839eabc11470815415f061cee0e9c/fb070/form-usage-1.png",
            "srcSet": ["/static/0fe839eabc11470815415f061cee0e9c/d6747/form-usage-1.png 288w", "/static/0fe839eabc11470815415f061cee0e9c/09548/form-usage-1.png 576w", "/static/0fe839eabc11470815415f061cee0e9c/fb070/form-usage-1.png 1152w", "/static/0fe839eabc11470815415f061cee0e9c/c3e47/form-usage-1.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "form-logic"
    }}>{`Form logic`}</h2>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Radio buttons`}</strong>{` are used when there is a list of two or more options that are mutually exclusive and the user must select exactly one choice. In other words, clicking a non-selected radio button will deselect whatever other button was previously selected in the list.`}</li>
      <li parentName="ul"><strong parentName="li">{`Checkboxes`}</strong>{` are used when there are lists of options and the user may select any number of choices, including zero, one, or several. In other words, each checkbox is independent of all other checkboxes in the list, so checking one box doesn’t uncheck the others. A stand-alone checkbox, or a toggle can be used for a single option that the user can turn on or off.`}</li>
      <li parentName="ul"><strong parentName="li">{`Select elements`}</strong>{` are used for fields in which a single selection is required and there are a large number of possible options.`}</li>
    </ul>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "35.44642857142858%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAABVUlEQVQoz2NgQAcsjAzEgv///4MwEwj//v2bGSwoZ6nKIMotxAYyik+InxUkJioqCqI5gJgZppmdnZ0FRIGwgYEBflvV0syZy3d38ub7pjGB+IqKisxHjx6Vu3jxoihMTVBQEPvp06d1jxw5IgwTu3DhgvDJkycrgXTzrVu3YuAG2p1K4Wq8MdMT5HQQ38zcjPPRo0cVz58/z799+zYXSCwxMVH4wYMHza9evXKE6Xvz5o3O169f/4PAu3fvbmC4VDnGGEw7ODqwAA00AxpoDaTBBq5fv57l2bNnxk+fPlWCqf/w4YPC69evj799+/b6p0+f5iUlJTEwSEhIMIiLi7M5M/DCDebh4WFCD3wgYELjMzg5OTElJydzHj9+nAXoE0h4i/ILM0hISTDGzM1luLXhPFhMXl6eoaGhgXnlypXMMM0mJiYMNTU1LFevXsUZIT4+PgwAHLyM+tBJhFsAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "form logic",
            "title": "form logic",
            "src": "/static/edc1d596c09fbfe374c5772bd92ac4d3/fb070/form-usage-4.png",
            "srcSet": ["/static/edc1d596c09fbfe374c5772bd92ac4d3/d6747/form-usage-4.png 288w", "/static/edc1d596c09fbfe374c5772bd92ac4d3/09548/form-usage-4.png 576w", "/static/edc1d596c09fbfe374c5772bd92ac4d3/fb070/form-usage-4.png 1152w", "/static/edc1d596c09fbfe374c5772bd92ac4d3/fb104/form-usage-4.png 1728w", "/static/edc1d596c09fbfe374c5772bd92ac4d3/8fefe/form-usage-4.png 2240w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>
    <h2 {...{
      "id": "validation--errors"
    }}>{`Validation & errors`}</h2>
    <h3 {...{
      "id": "error-messaging"
    }}>{`Error messaging`}</h3>
    <p>{`Effective error messaging can help the user to understand the problem and how to fix it. First, inform the user what has happened, then provide guidance on next steps or possible resolutions. `}<a parentName="p" {...{
        "href": "/components/notification"
      }}>{`Inline notifications`}</a>{` can be used to state the general problem with the users input, such as `}<em parentName="p">{`“Please input the required fields.”`}</em>{` Inline Notifications can occur pre or post submission, depending on the type of data the user is inputting.`}</p>
    <h3 {...{
      "id": "form-validation"
    }}>{`Form validation`}</h3>
    <p>{`We recommend validating the users data before form submission. Use visual cues to guide the user as to where the problem lies within the form. This will help to easily identify the elements that need to be corrected.`}</p>
    <p>{`The validation should appear when the user has clicked away from the text field. Once the user corrects the errors within the text field, the validation should disappear once the data is rendered as valid.`}</p>
    <p>{`The validation label below the field should be as informative as possible when describing the issue with the users data. For example, if password limitations require 16 characters, but the user inputs a password with only six characters, the text should read something like, `}<em parentName="p">{`“Password must be at least 16 characters.”`}</em></p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <img {...{
          "src": "/ee35d10c18135c7a87a0d560ba9b2b75/form-validation.gif",
          "alt": "form validation"
        }}></img>
      </Column>
    </Row>
    <h3 {...{
      "id": "optional-vs-required-fields"
    }}>{`Optional vs. required fields`}</h3>
    <p>{`All fields in a form are assumed required, with optional fields being tagged as so.`}</p>
    <Row mdxType="Row">
      <Column colLg={8} mdxType="Column">
        <span {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1152px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "100.95108695652175%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAC+klEQVQ4y62US2gTURSG7ySTPsJYiw9aIkQRXbjqSii0dCEuiht3QRRam74QKrqVKritQTfSrUvbglBE66KIElNjaUZEoYuStknaJpm8msfEtMEm1//cJEOklFp04OO/c+bmn/O4E8ZwtbW1sZrW1vXxI10dHR1CbTabEVMUhdntdrF2OByG1qiP77u6urqE9vb2Sp2dne3d3d22bDYrFwoFmVTXdUsmkzFxziVgLhaLtbhBLpcTHlhXTLGR/etlmCUSCaGlUql5c3PzRSwWe5PP56ewYRq8BG+RrWNjY+MW+IBspqvPaM8rMAkUwxTp17Js3NraeqBp2tPt7W1XKpUiJsBkOp2+Gg6HrwSDwedYT1SfPQHPwENgJQ8oY2NjY8Kwp6dHcrlcLdQnQD0z7ezsmGEkUQaoQKE+ogozkjDhpfUID6F1GSrRaHQRWYTX19cDyDaEYYTwnAhWCdVB9xGwCFrJAyoxVVWF4crKiuT1es95PJ6Lbrf7wvLy8ln0zH4ItOcMMJEHlDGUIAyhFuBEiffBXTR/GMNxgsEDcKIVw+AGaNw3FBhYsZ4FSyj1M/qxeAhfMCAVe1+DFvKASv/tHBoeYtS4yuWyNRKJzIVCoR8o2YsBecACjtEC4gu0RlxoNf4J9yrO8RyqOk4eyWRSorql6htaYJZAH/na2hrHtDmmzQOBgFCKra6uGvd+v5/DgKOXCbTrZLWHfxpiSjEyqv8xKRmQ0suQHde0GI9qcZ7J6jydySX1fEEY4l6iRoqScYitKP9dPB7/jjJ8UCpHJSXwSap4ruZ0/evPfG6Jl7PvgZv/Sk3t6WExFL4bl9jMzIww7OvrYz6fT8aPZWQooz8yMpZhJCMz8Q8zO69a+LzVdOleoPXY6O5160jR0TRUvCb36w3k0eAsVAYzPj7+t7OsyE1+XrnDY/IwRnub+02D/ISID3CJ4RMTa9LD+PhNw3/iI3b5cVFm/aXT5sFye/No6ZRlaE/MoWmkdPTzZh464MwOVOK/AQ6OGu6LpPIMAAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "optional fields in a form",
            "title": "optional fields in a form",
            "src": "/static/bee9f05a5404998d880f60ef7273451a/fb070/form-usage-5.png",
            "srcSet": ["/static/bee9f05a5404998d880f60ef7273451a/d6747/form-usage-5.png 288w", "/static/bee9f05a5404998d880f60ef7273451a/09548/form-usage-5.png 576w", "/static/bee9f05a5404998d880f60ef7273451a/fb070/form-usage-5.png 1152w", "/static/bee9f05a5404998d880f60ef7273451a/c3e47/form-usage-5.png 1472w"],
            "sizes": "(max-width: 1152px) 100vw, 1152px",
            "loading": "lazy"
          }}></img>{`
    `}</span>
      </Column>
    </Row>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      